import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import PageHeader from "@/components/PageHeader";
import { AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAdminTheme } from "@/context/AdminThemeContext";
import { OSM_TILE_URL, OSM_ATTRIBUTION, useMapProvider, useGoogleMapsSettings, MapProviderToggle } from "@/lib/mapProvider";
import GoogleAccountsMap from "@/components/GoogleAccountsMap";

const STATUS_COLOR = {
  active: "#00E676",
  pending_install: "#4DA6FF",
  paused: "#8B949E",
  suspended: "#FFB020",
  disabled: "#FF5252",
  terminated: "#FF5252",
};

// react-leaflet's default marker icon references image URLs that webpack
// doesn't resolve automatically -- a plain colored dot per status sidesteps
// that bundling gotcha entirely and gives us status color-coding for free.
function statusIcon(status) {
  const color = STATUS_COLOR[status] || "#8B949E";
  return L.divIcon({
    className: "",
    html: `<div style="width:14px;height:14px;border-radius:50%;background:${color};border:2px solid rgba(0,0,0,0.35);box-shadow:0 0 0 2px rgba(255,255,255,0.5)"></div>`,
    iconSize: [14, 14],
    iconAnchor: [7, 7],
    popupAnchor: [0, -8],
  });
}

export default function PppoeMap() {
  const [data, setData] = useState(null);
  const { theme } = useAdminTheme();
  const [mapProvider, setMapProvider] = useMapProvider();
  const googleMaps = useGoogleMapsSettings();
  const navigate = useNavigate();

  useEffect(() => {
    if (googleMaps.loaded && !googleMaps.valid && mapProvider === "google") setMapProvider("osm");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [googleMaps.loaded, googleMaps.valid]);

  useEffect(() => {
    api.get("/admin/pppoe/map")
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, []);

  const center = useMemo(() => {
    const pts = data?.accounts || [];
    if (pts.length === 0) return [-1.286389, 36.817223]; // Nairobi fallback
    const lat = pts.reduce((a, p) => a + p.gps_lat, 0) / pts.length;
    const lng = pts.reduce((a, p) => a + p.gps_lng, 0) / pts.length;
    return [lat, lng];
  }, [data]);

  return (
    <div className="flex flex-col h-screen">
      <PageHeader
        testId="pppoe-map-header" title="PPPoE Map" subtitle="Installations by GPS location"
        actions={googleMaps.valid ? <MapProviderToggle provider={mapProvider} onChange={setMapProvider} /> : null}
      />

      {data && data.missing_gps_count > 0 && (
        <div className="mx-4 md:mx-8 mt-4 border border-nhp-warning/40 bg-nhp-warning/10 text-nhp-warning text-xs font-mono px-4 py-2 flex items-center gap-2 shrink-0">
          <AlertTriangle className="h-3.5 w-3.5" />
          {data.missing_gps_count} account{data.missing_gps_count === 1 ? "" : "s"} without GPS coordinates on file — not shown on the map.
        </div>
      )}

      <div className="flex-1 p-4 md:p-8 min-h-0">
        <div className={`border border-nhp-border bg-nhp-surface h-full ${theme === "dark" && mapProvider === "osm" ? "leaflet-invert-tiles" : ""}`}>
          {!data ? (
            <div className="h-full flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-nhp-muted" /></div>
          ) : mapProvider === "google" ? (
            <GoogleAccountsMap apiKey={googleMaps.apiKey} accounts={data.accounts} center={center} zoom={12} onSelect={(id) => navigate(`/admin/pppoe-accounts/${id}`)} />
          ) : (
            <MapContainer center={center} zoom={12} style={{ height: "100%", width: "100%" }}>
              <TileLayer url={OSM_TILE_URL} attribution={OSM_ATTRIBUTION} />
              {data.accounts.map((a) => (
                <Marker key={a.id} position={[a.gps_lat, a.gps_lng]} icon={statusIcon(a.status)}>
                  <Popup>
                    <div className="font-mono text-xs">
                      <div className="font-bold">{a.pppoe_username}</div>
                      <div>{a.full_name || a.phone}</div>
                      <div className="uppercase text-[10px] tracking-wider mt-1">{a.status.replace("_", " ")}</div>
                      <button
                        type="button"
                        onClick={() => navigate(`/admin/pppoe-accounts/${a.id}`)}
                        className="mt-2 underline text-[11px]"
                      >
                        View profile →
                      </button>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          )}
        </div>
      </div>
    </div>
  );
}
